import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell, Icon } from '@ui-kit';
import type { BreadcrumbItem, AppShellLabels } from '@ui-kit';
import { AuthGuard } from './AuthGuard';
import { ThemeToggle } from './ThemeToggle';
import { useNavMenu } from './hooks/useNavMenu';
import { APP_VERSION } from './appVersion';
import { useSessionRefresh } from './auth/useSessionRefresh';
import { sessionEvents } from './auth/sessionEvents';
import { useCommandPalette } from './hooks/useCommandPalette';
import { CommandPalette } from './components/CommandPalette';
import { CommandPaletteProvider } from './context/CommandPaletteContext';
import { ScaffoldCartBadge } from './components/ScaffoldCartBadge';
// queue #691 §R691-1: cross-feature import 선례(useSearchIndex.ts 가 common-showcase 를 import) —
// 역방향(AuthLayout 이 common-showcase 컴포넌트를 import)도 동일 경계.
import { FeedbackAnswerBadge } from '../common-showcase/components/FeedbackAnswerBadge';

interface AuthLayoutProps {
  children: ReactNode;
}

/** 경로 → i18n 키 매핑 */
const PATH_TO_KEY: Record<string, string> = {
  '/_index': 'breadcrumb._index',
  '/gateway': 'breadcrumb.gateway',
  '/gateway/overview': 'breadcrumb.gateway.overview',
  '/gateway/services': 'breadcrumb.gateway.services',
  '/gateway/routes': 'breadcrumb.gateway.routes',
  '/gateway/apis': 'breadcrumb.gateway.apis',
  '/gateway/targets': 'breadcrumb.gateway.targets',
  '/gateway/plugins': 'breadcrumb.gateway.plugins',
  '/gateway/request-logs': 'breadcrumb.gateway.request-logs',
  '/logout': 'breadcrumb.logout',
  '/logout/inspector': 'breadcrumb.logout.inspector',
  '/dev': 'breadcrumb.dev',
  '/dev/token-inspector': 'breadcrumb.dev.token-inspector',
  '/permission-demo': 'breadcrumb.permission-demo',
  '/showcase': 'breadcrumb.showcase',
  '/error-demo': 'breadcrumb.error-demo',
  '/attachments': 'breadcrumb.attachments',
  '/event-monitor': 'breadcrumb.event-monitor',
  '/users': 'breadcrumb.users',
  '/me': 'breadcrumb.me',
  '/audit-logs': 'breadcrumb.audit-logs',
  '/audit-logs/me': 'breadcrumb.audit-logs.me',
  '/roles': 'breadcrumb.roles',
  '/codes': 'breadcrumb.codes',
  '/menus': 'breadcrumb.menus',
  '/system-settings': 'breadcrumb.system-settings',
  '/standards': 'breadcrumb.standards',
  '/getting-started': 'breadcrumb.getting-started',
  '/scaffold': 'breadcrumb.scaffold',
};

function buildBreadcrumb(
  pathname: string,
  t: (key: string) => string
): BreadcrumbItem[] {
  const key = PATH_TO_KEY[pathname];
  if (!key) return [{ label: t('breadcrumb.home'), path: '/_index' }];

  if (pathname === '/_index') {
    return [{ label: t('breadcrumb._index') }];
  }

  // 개발자도구 그룹 페이지 (사이드바 §3 기준)
  const DEV_TOOL_PATHS = new Set([
    '/permission-demo',
    '/showcase',
    '/error-demo',
    '/attachments',
    '/event-monitor',
  ]);
  if (DEV_TOOL_PATHS.has(pathname)) {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.devtools') },
      { label: t(key) },
    ];
  }

  if (pathname.startsWith('/gateway/')) {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.devtools') },
      { label: t(key) },
    ];
  }

  if (pathname.startsWith('/dev/')) {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.devtools') },
      { label: t(key) },
    ];
  }

  if (pathname === '/system-settings') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.system-settings') },
    ];
  }

  if (pathname === '/standards') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.standards') },
    ];
  }

  if (pathname.startsWith('/users/')) {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.users'), path: '/users' },
      { label: t('breadcrumb.users.detail') },
    ];
  }

  if (pathname === '/users') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.users') },
    ];
  }

  if (pathname === '/audit-logs') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.audit-logs') },
    ];
  }

  if (pathname === '/audit-logs/me') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.audit-logs.me') },
    ];
  }

  if (pathname === '/roles') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.roles') },
    ];
  }

  if (pathname.startsWith('/roles/')) {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.roles'), path: '/roles' },
      { label: t('breadcrumb.roles.detail') },
    ];
  }

  if (pathname === '/codes') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.codes') },
    ];
  }

  if (pathname === '/menus') {
    return [
      { label: t('breadcrumb.home'), path: '/_index' },
      { label: t('breadcrumb.system') },
      { label: t('breadcrumb.menus') },
    ];
  }

  return [
    { label: t('breadcrumb.home'), path: '/_index' },
    { label: t(key) },
  ];
}

/**
 * AuthLayoutInner — mounts session management hooks.
 * Rendered inside AuthGuard so hooks only run for authenticated users.
 */
function AuthLayoutInner({ children }: AuthLayoutProps) {
  const { t, ready } = useTranslation();
  const { groups, currentUser } = useNavMenu();
  const location = useLocation();
  const navigate = useNavigate();

  // #477 — silent refresh: schedule proactive token renewal
  useSessionRefresh();

  // #659 — 전역 커맨드 팔레트: 헤더 검색 버튼 + Ctrl/Cmd+K 로 오픈
  const commandPalette = useCommandPalette();

  // #476 — subscribe to global session-expired events emitted by the
  // authInterceptor when any API call returns 401.
  useEffect(() => {
    const unsubscribe = sessionEvents.on((event) => {
      if (event.type === 'SESSION_EXPIRED') {
        const returnUrl = encodeURIComponent(
          event.returnUrl ?? location.pathname
        );
        navigate(`/login?expired=1&returnUrl=${returnUrl}`, { replace: true });
      }
    });
    return unsubscribe;
    // navigate is stable; location.pathname should not re-subscribe on every
    // path change — capture once, the handler uses window.location fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const breadcrumb = buildBreadcrumb(location.pathname, t);

  // 아직 유저 로딩 중이면 AuthGuard 안에서 처리됨
  const fallbackUser = currentUser ?? {
    id: 0,
    displayName: '...',
    email: '',
    role: 'ROLE_USER' as const,
  };

  const appShellLabels: AppShellLabels = {
    sidenavAriaLabel: t('appshell.sidenav.aria'),
    brandName: t('appshell.sidenav.brand'),
    menuToggleAriaLabel: t('appshell.header.toggle'),
    langSwitchAriaLabel: (currentLang: 'ko' | 'en') => {
      // 항상 "언어 전환" 문자열을 포함해야 E2E getByRole(/언어 전환/) 셀렉터가 동작함.
      // i18n 언어가 en 으로 전환되면 t() 가 영문 문자열을 반환하므로
      // "언어 전환" prefix 를 하드코딩으로 고정.
      const currentLabel =
        currentLang === 'ko'
          ? t('appshell.header.lang.ko_label')
          : t('appshell.header.lang.en_label');
      return `언어 전환 — ${currentLabel}`;
    },
    notificationsAriaLabel: t('appshell.header.notifications'),
    hideNotifications: true,
    userMenuAriaLabel: (displayName: string) =>
      t('appshell.user.menu.aria', { name: displayName }),
    profileLabel: t('appshell.user.menu.profile'),
    passwordChangeLabel: t('appshell.user.menu.password_change'),
    activityHistoryLabel: t('appshell.user.menu.activity_history'),
    logoutLabel: t('appshell.user.menu.logout'),
    // i18n Suspense race 방어: ready=false 시 한국어 fallback 으로 직접 채움.
    // (i18n.ts 는 동기 초기화이므로 실제 race 가능성은 낮지만 방어적으로 적용)
    roleLabels: ready
      ? {
          ROLE_GUEST: t('appshell.user.role.guest'),
          ROLE_ADMIN: t('appshell.user.role.admin'),
          ROLE_MANAGER: t('appshell.user.role.manager'),
          ROLE_USER: t('appshell.user.role.user'),
        }
      : {
          ROLE_GUEST: '게스트',
          ROLE_ADMIN: '관리자',
          ROLE_MANAGER: '업무 관리자',
          ROLE_USER: '사용자',
        },
    sidenavGroupCollapseLabel: t('appshell.sidenav.group.collapse'),
    sidenavGroupExpandLabel: t('appshell.sidenav.group.expand'),
    breadcrumbAriaLabel: t('appshell.breadcrumb.aria'),
    mainAriaLabel: t('appshell.main.aria'),
    footerCopyright: t('appshell.footer.copyright', { year: new Date().getFullYear() }),
    footerVersionLabel: (version: string) => `v${version}`,
    footerBuildLabel: t('appshell.footer.build_label'),
  };

  return (
    <CommandPaletteProvider value={{ open: commandPalette.open }}>
      <AppShell
        sideNavItems={groups}
        user={fallbackUser}
        currentPath={location.pathname}
        breadcrumb={breadcrumb}
        onNavigate={(path) => navigate(path)}
        onLogout={() => navigate('/logout')}
        onProfileClick={() => navigate('/me')}
        onPasswordChange={() => navigate('/password-reset/request')}
        onActivityHistory={() => navigate('/audit-logs/me')}
        version={APP_VERSION}
        themeToggle={<ThemeToggle />}
        headerActions={
          <>
            {/* queue #691 §R691-0②: 문의 답변 알림 배지 — count 0 이면 컴포넌트 자체가 미렌더
                (카트 배지 좌측 · 좌→우 [🔔][📦][🔍][🌗] 순 · §R691-2(c)) */}
            <FeedbackAnswerBadge />
            {/* queue #682 §R682-4: 담기 카트 배지 — count 0 이면 컴포넌트 자체가 미렌더 */}
            <ScaffoldCartBadge />
            <button
              type="button"
              onClick={commandPalette.open}
              aria-label={`${t('shared.search.trigger')} (Ctrl+K)`}
              title={`${t('shared.search.trigger')} (Ctrl+K)`}
              data-tour="global-search"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '4px var(--spacing-2)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                transition: 'background 0.15s',
              }}
            >
              <Icon id="search" size={14} />
              <span aria-hidden="true">Ctrl+K</span>
            </button>
          </>
        }
        labels={appShellLabels}
      >
        {children}
      </AppShell>
      <CommandPalette isOpen={commandPalette.isOpen} onClose={commandPalette.close} />
    </CommandPaletteProvider>
  );
}

/**
 * AuthLayout — the authenticated shell wrapper.
 * AuthGuard renders first; if auth passes, AuthLayoutInner mounts the
 * AppShell + session-refresh + event subscription.
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <AuthGuard>
      <AuthLayoutInner>{children}</AuthLayoutInner>
    </AuthGuard>
  );
}
